import { createSlice } from "@reduxjs/toolkit"

// Initialize state from localStorage if available
const getInitialState = () => {
    try {
        // Check for token in localStorage directly (simpler and more reliable)
        // This avoids require() issues in browser environment
        const hasToken = !!(localStorage.getItem('accessToken') || localStorage.getItem('token'));
        const stored = localStorage.getItem("user_sis");
        
        // Debug: Log what we found
        console.log('🔍 Redux admin - getInitialState check:', {
            hasStored: !!stored,
            storedLength: stored ? stored.length : 0,
            hasToken: hasToken
        });
        
        if (stored && stored !== null && stored !== undefined && stored !== "") {
            try {
                const parsed = JSON.parse(stored);
                const keys = Object.keys(parsed || {});
                console.log('✅ Redux admin - Initial state loaded from localStorage');
                console.log('✅ Redux admin - Initial state keys count:', keys.length);
                console.log('✅ Redux admin - Has token:', hasToken);
                console.log('✅ Redux admin - Keys:', keys);
                
                // If we have a token, we can trust the data (even if incomplete)
                if (hasToken) {
                    console.log('✅ Token found - using localStorage data');
                    return parsed;
                }
                
                // No token but data exists - might be from old session
                // If we only have 2 fields (schoolID and schoolname), this is incomplete/old data
                // Keep it temporarily to prevent logout - login will update with full data
                if (keys.length <= 2 && keys.includes('schoolID') && keys.includes('schoolname')) {
                    console.warn('⚠️ WARNING: Initial state has only 2 fields (incomplete data).');
                    console.warn('⚠️ Incomplete data:', parsed);
                    console.warn('⚠️ Keeping temporarily - login will update with full data.');
                    // Keep incomplete data to prevent logout - login process will update it
                    return parsed;
                }
                
                // If we have proper data (more than 2 fields), use it
                if (keys.length > 2) {
                    console.log('✅ Valid initial state loaded with', keys.length, 'fields');
                    console.log('✅ Data includes smsID:', !!parsed.smsID, 'positiondescription:', parsed.positiondescription);
                    return parsed;
                }
                
                // Fallback: if data exists but doesn't meet criteria, still use it but warn
                console.warn('⚠️ Using localStorage data but it may be incomplete');
                return parsed;
            } catch (parseError) {
                console.error('❌ Error parsing localStorage user_sis:', parseError);
                console.error('❌ Raw stored value:', stored);
                // Don't clear corrupted data - let login process handle it
                // Return default state but don't clear localStorage
            }
        } else {
            // No localStorage data - this is normal on first page load before login
            console.log('ℹ️ Redux admin - No localStorage data found (normal on first load before login)');
        }
    } catch (e) {
        console.error('❌ Error in getInitialState:', e);
        // Don't clear corrupted data - let login process handle it
    }
    console.log('ℹ️ Redux admin - Using default initial state (no localStorage data)');
    return { user: "jngissah" };
};

const Insider = createSlice({

    name: "todos",
    initialState: getInitialState(),
    reducers: {
        login: (state, action) => {
            // Update state with the login data - ensure ALL fields are stored
            console.log('🔵 Redux login reducer - action.payload received');
            
            if (action.payload && action.payload.data) {
                const incomingData = action.payload.data;
                const incomingKeys = Object.keys(incomingData || {});
                
                console.log('🔵 Redux login reducer - incoming data keys count:', incomingKeys.length);
                console.log('🔵 Redux login reducer - incoming data keys:', incomingKeys);
                
                // Create a new state object with ALL fields from the login response
                // Use spread operator to ensure all properties are copied, including nested objects
                const newState = { ...incomingData };
                
                // Ensure positiondescription is always present (default for School admins)
                if (!newState.positiondescription) {
                    newState.positiondescription = "Administrator";
                    console.log('⚠️ Redux: Added default positiondescription for School admin');
                }
                
                console.log('🔵 Redux login reducer - newState created with', Object.keys(newState).length, 'fields');
                
                // CRITICAL: Update localStorage FIRST with ALL fields before returning state
                // This ensures localStorage always has the complete data
                try {
                    const dataToStore = JSON.stringify(newState);
                    localStorage.setItem('user_sis', dataToStore);
                    console.log('✅ Redux login reducer - saved to localStorage');
                    console.log('✅ Data size:', dataToStore.length, 'bytes');
                    
                    // CRITICAL VERIFICATION: Ensure all fields were stored correctly
                    const verify = JSON.parse(localStorage.getItem('user_sis'));
                    const verifyKeys = Object.keys(verify || {});
                    console.log('✅ Redux login reducer - verification: stored', verifyKeys.length, 'fields');
                    
                    // Verify we have all expected fields including critical ones for Finance menu
                    const expectedFields = ['_id', 'schoolID', 'schoolname', 'email', 'contact', 'address', 
                                          'image', 'country', 'currency', 'locale', 'status', 'terms', 'smsID', 'positiondescription'];
                    const missingFields = expectedFields.filter(field => !verifyKeys.includes(field));
                    
                    if (missingFields.length > 0) {
                        console.warn('⚠️ Missing expected fields:', missingFields);
                    }
                    
                    // Critical check: smsID is needed for Finance menu visibility
                    if (!verify.smsID && !verify.positiondescription) {
                        console.error('❌ CRITICAL: Both smsID and positiondescription are missing! Finance menu will not appear.');
                        console.error('Current admin state:', verify);
                    } else if (!verify.smsID) {
                        console.warn('⚠️ WARNING: smsID is missing, but positiondescription might allow Finance menu:', verify.positiondescription);
                    } else {
                        console.log('✅ Finance menu should be visible - smsID:', verify.smsID, 'positiondescription:', verify.positiondescription);
                    }
                    
                    if (verifyKeys.length < 20) {
                        console.error('❌ ERROR: Only', verifyKeys.length, 'fields stored! Expected at least 20');
                        console.error('Re-storing data...');
                        localStorage.setItem('user_sis', dataToStore);
                        const recheck = JSON.parse(localStorage.getItem('user_sis'));
                        const recheckKeys = Object.keys(recheck || {});
                        console.log('After re-store - keys count:', recheckKeys.length);
                        console.log('After re-store - has smsID:', !!recheck.smsID);
                    } else {
                        console.log('✅ SUCCESS: All', verifyKeys.length, 'fields stored in localStorage and Redux');
                        console.log('✅ Finance menu will be visible (smsID present):', !!verify.smsID);
                    }
                } catch (e) {
                    console.error('❌ CRITICAL ERROR saving to localStorage:', e);
                    // Even if localStorage fails, still return the new state for Redux
                }
                
                // Return the new state with ALL fields - this completely replaces the Redux state
                console.log('✅ Redux login reducer - returning new state with', Object.keys(newState).length, 'fields');
                return newState;
            }
            console.warn('⚠️ Redux login reducer - no data in payload, returning existing state');
            return state;
        },
        logout: (state) => {
            // Clear all admin data on logout
            console.log('🔴 Redux logout reducer - clearing admin state');
            // Clear localStorage
            localStorage.removeItem('user_sis');
            // Return default state
            return { user: "jngissah" };
        }

    },

})

export const { login, logout } = Insider.actions;
export default Insider.reducer;